import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { FIXTURE_SCOPES, SEASON_PHASE_META } from "../constants";
import {
  fetchAdminLeagueDetail,
  fetchAdminLeagueFixtures,
  syncAdminFixture,
  syncAdminLeague,
  syncAdminLeagueFixtures,
  updateAdminLeague,
} from "../api/fetchAdminLeagues";
import { FixtureRow, FixturesListHeader } from "./FixturesList";
import LinkedActivityPanel from "./LinkedActivityPanel";
import DisableLeagueModal from "../DisableLeagueModal";
import { formatDate, formatRelative } from "../helpers";

function DetailStat({ label, value }) {
  return (
    <div className="amf-desk-stat">
      <span className="amf-desk-stat-val">{value}</span>
      <span className="amf-desk-stat-key">{label}</span>
    </div>
  );
}

export default function LeaguesFloorDetail({
  league,
  busy,
  onUpdated,
  onToggleEnabled,
}) {
  const [scope, setScope] = useState("upcoming");
  const [fixtures, setFixtures] = useState([]);
  const [fxPagination, setFxPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [fxPage, setFxPage] = useState(1);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxSyncingId, setFxSyncingId] = useState(null);
  const [fixtureWindow, setFixtureWindow] = useState("close");
  const [fixtureWindowSyncing, setFixtureWindowSyncing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [notes, setNotes] = useState("");
  const [seasonYear, setSeasonYear] = useState("");
  const [saving, setSaving] = useState(false);
  const [panel, setPanel] = useState("fixtures");
  const [linkedActivity, setLinkedActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  useEffect(() => {
    setNotes(league?.notes || "");
    setSeasonYear(String(league?.currentSeasonYear || ""));
    setScope("upcoming");
    setFxPage(1);
    setLinkedActivity(null);
  }, [league?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!league?._id) return undefined;
    let cancelled = false;
    setActivityLoading(true);
    fetchAdminLeagueDetail(league._id)
      .then((data) => {
        if (!cancelled) setLinkedActivity(data?.league?.linkedActivity || null);
      })
      .catch(() => {
        if (!cancelled) setLinkedActivity(null);
      })
      .finally(() => {
        if (!cancelled) setActivityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [league?._id]);

  const loadFixtures = useCallback(
    async (pageNum = 1) => {
      if (!league?._id) return;
      setFxLoading(true);
      try {
        const data = await fetchAdminLeagueFixtures(league._id, {
          page: pageNum,
          limit: 20,
          scope,
        });
        setFixtures(data?.fixtures || []);
        setFxPagination((prev) => ({
          ...prev,
          ...(data?.pagination || {}),
        }));
      } catch (err) {
        setFixtures([]);
        toast.error(err.response?.data?.error || "Failed to load fixtures");
      } finally {
        setFxLoading(false);
      }
    },
    [league?._id, scope]
  );

  useEffect(() => {
    if (!league?._id) return;
    loadFixtures(fxPage);
  }, [league?._id, scope, fxPage, loadFixtures]);

  useEffect(() => {
    setFxPage(1);
  }, [scope, league?._id]);

  const handleFixtureSync = async (fixture) => {
    if (!fixture?._id) return;
    setFxSyncingId(fixture._id);
    try {
      const data = await syncAdminFixture(fixture._id);
      setFixtures((rows) =>
        rows.map((row) => (row._id === fixture._id ? data.fixture : row))
      );

      const { changes, impact } = data;
      let message = "Fixture updated from API-Sports";
      if (changes?.statusChanged) {
        message += ` · ${changes.previousStatus} → ${changes.newStatus}`;
      }
      if (impact?.hasLinkedActivity) {
        message += ` · ${impact.pendingBettings} pending bet(s), ${impact.activeP2P} P2P, ${impact.openMarkets} market(s) linked`;
      }
      toast.success(message);
      if (data.settlementNote) {
        toast.info(data.settlementNote, { autoClose: 7000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Fixture sync failed");
    } finally {
      setFxSyncingId(null);
    }
  };

  if (!league) {
    return (
      <section className="amf-detail amf-detail-idle alg-detail" aria-label="Select a league">
        <div className="amf-idle">
          <p className="amf-idle-eyebrow">Leagues desk</p>
          <p className="amf-idle-title">Select a league from the ledger</p>
          <p className="amf-idle-copy">Fixtures, season sync, and league settings.</p>
        </div>
      </section>
    );
  }

  const phaseMeta = SEASON_PHASE_META[league.seasonPhase] || SEASON_PHASE_META.unknown;

  const handleSync = async () => {
    setSyncing(true);
    try {
      const data = await syncAdminLeague(league._id);
      toast.success("Metadata synced");
      onUpdated(data.league);
      loadFixtures(fxPage);
    } catch (err) {
      toast.error(err.response?.data?.error || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleFixturesWindowSync = async () => {
    if (!league?._id) return;
    setFixtureWindowSyncing(true);
    try {
      const data = await syncAdminLeagueFixtures(league._id, { window: fixtureWindow });
      const range = data.dateRange
        ? ` (${data.dateRange.startDate} → ${data.dateRange.endDate})`
        : "";
      toast.success(
        `Synced ${data.processedCount ?? 0}/${data.totalCount ?? 0} fixture(s)${range}`
      );
      if (data.errorCount) {
        toast.warn(`${data.errorCount} fixture sync error(s). Check backend logs for details.`);
      }
      if (data.settlementNote) {
        toast.info(data.settlementNote, { autoClose: 7000 });
      }
      if (data.league) {
        onUpdated(data.league);
      }
      setFxPage(1);
      await loadFixtures(1);
    } catch (err) {
      toast.error(err.response?.data?.error || "Fixture window sync failed");
    } finally {
      setFixtureWindowSyncing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { notes };
      if (seasonYear && Number(seasonYear) !== league.currentSeasonYear) {
        body.currentSeasonYear = Number(seasonYear);
      }
      const data = await updateAdminLeague(league._id, body);
      toast.success("League updated");
      onUpdated(data.league);
    } catch (err) {
      toast.error(err.response?.data?.error || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRequest = () => {
    if (!league) return;
    const nextEnabled = !league.isEnabled;
    if (!nextEnabled && linkedActivity?.hasLinkedActivity) {
      setDisableOpen(true);
      return;
    }
    onToggleEnabled(nextEnabled, linkedActivity);
  };

  const handleForceDisable = () => {
    onToggleEnabled(false, linkedActivity, { force: true });
    setDisableOpen(false);
  };

  return (
    <>
    <section className="amf-detail alg-detail" aria-label="League detail">
      <header className="amf-desk amf-desk-with-question alg-desk-header">
        <div className="alg-desk-identity">
          <span className="alg-desk-logo">
            {league.logo ? (
              <img src={league.logo} alt="" />
            ) : (
              <span className="alg-row-logo-fallback">{league.name?.[0] || "?"}</span>
            )}
          </span>
          <div className="alg-desk-copy">
            <p className="amf-desk-eyebrow">
              {league.country?.name || "—"} · API {league.apiId}
            </p>
            <h2 className="amf-desk-title alg-desk-title">{league.name}</h2>
          </div>
        </div>

        <div className="alg-desk-trail">
          <div className="amf-desk-stats alg-desk-stats">
            <DetailStat label="Fixtures" value={league.fixtureCount ?? 0} />
            <DetailStat label="Season" value={league.currentSeasonYear ?? "—"} />
            <DetailStat label="Status" value={league.isEnabled ? "Active" : "Off"} />
          </div>
          <div className="alg-desk-controls">
            <button
              type="button"
              className={`alg-toggle${league.isEnabled ? " is-on" : ""}${linkedActivity?.hasLinkedActivity ? " is-locked" : ""}`}
              onClick={handleToggleRequest}
              disabled={busy || activityLoading}
              title={
                league.isEnabled && linkedActivity?.hasLinkedActivity
                  ? "Linked activity must be cleared or force-disabled"
                  : league.isEnabled
                    ? "Disable league"
                    : "Enable league"
              }
            >
              <span className="alg-toggle-knob" />
            </button>
            <button type="button" className="alg-btn" onClick={handleSync} disabled={syncing || busy}>
              {syncing ? <Loader2 size={12} className="am-spin" /> : <RefreshCw size={12} />}
              Sync
            </button>
          </div>
        </div>
      </header>

      <div className="amf-detail-scroll alg-detail-scroll">
        <div className="alg-detail-timing">
          <div className="alg-detail-time">
            <span className="alg-detail-time-key">Phase</span>
            <span className="alg-detail-time-val">{phaseMeta.label}</span>
          </div>
          <div className="alg-detail-time">
            <span className="alg-detail-time-key">Season window</span>
            <span className="alg-detail-time-val">
              {formatDate(league.currentSeasonStartDate)} → {formatDate(league.currentSeasonEndDate)}
            </span>
          </div>
          <div className="alg-detail-time">
            <span className="alg-detail-time-key">Last synced</span>
            <span className="alg-detail-time-val">{formatRelative(league.lastSyncedAt)}</span>
          </div>
        </div>

        {activityLoading ? (
        <div className="alg-linked-activity-loading">
          <Loader2 size={14} className="am-spin" aria-hidden />
          <span>Loading linked activity…</span>
        </div>
      ) : (
        <LinkedActivityPanel activity={linkedActivity} />
      )}

      <div className="alg-detail-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={panel === "fixtures"}
          className={panel === "fixtures" ? "is-on" : ""}
          onClick={() => setPanel("fixtures")}
        >
          Fixtures
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={panel === "settings"}
          className={panel === "settings" ? "is-on" : ""}
          onClick={() => setPanel("settings")}
        >
          Settings
        </button>
      </div>

      {panel === "fixtures" ? (
        <div className="alg-fixtures-panel">
          <div className="alg-fixtures-toolbar">
            <div className="alg-scope-switch">
              {FIXTURE_SCOPES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={scope === item.id ? "is-on" : ""}
                  onClick={() => setScope(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <span className="alg-fixtures-count tabular-nums">
              {fxPagination.total ?? 0} in DB
            </span>
            <div className="alg-fixtures-sync-controls">
              <select
                className="alg-fixture-window-select"
                value={fixtureWindow}
                onChange={(e) => setFixtureWindow(e.target.value)}
                disabled={fixtureWindowSyncing}
                aria-label="Fixture sync window"
              >
                <option value="close">Close window · -7d to +7d</option>
                <option value="long">Long window · today to +30d</option>
              </select>
              <button
                type="button"
                className="alg-fixture-window-sync"
                onClick={handleFixturesWindowSync}
                disabled={fixtureWindowSyncing || fxLoading}
                title="Pull fixtures for this league using the selected app sync window"
              >
                {fixtureWindowSyncing ? (
                  <Loader2 size={12} className="am-spin" aria-hidden />
                ) : (
                  <RefreshCw size={12} aria-hidden />
                )}
                Sync fixtures
              </button>
            </div>
          </div>

          <p className="alg-fixtures-hint">
            Sync pulls live status, score, and kickoff from API-Sports. Settlement for linked
            bets runs on the normal job schedule — not instantly.
          </p>

          {fixtures.length > 0 ? <FixturesListHeader /> : null}

          <div className={`alg-fixtures-list${fxLoading ? " is-loading" : ""}`}>
            {fxLoading && fixtures.length === 0 ? (
              <div className="alg-index-loading">
                <Loader2 size={18} className="am-spin" />
                <span>Loading fixtures…</span>
              </div>
            ) : fixtures.length === 0 ? (
              <div className="amf-index-empty">
                No {scope === "all" ? "" : `${scope} `}fixtures in the database for this league.
                {league.isEnabled ? " Run sync jobs or wait for the next cron pull." : " Enable the league first."}
              </div>
            ) : (
              fixtures.map((fx) => (
                <FixtureRow
                  key={fx._id}
                  fixture={fx}
                  syncing={fxSyncingId === fx._id}
                  onSync={handleFixtureSync}
                />
              ))
            )}
          </div>

          {fxPagination.totalPages > 1 ? (
            <div className="amf-index-foot alg-fixtures-foot">
              <button
                type="button"
                className="amf-index-page"
                disabled={!fxPagination.hasPrev || fxLoading}
                onClick={() => setFxPage((p) => p - 1)}
              >
                <ChevronLeft size={14} />
              </button>
              <span className="amf-index-page-label">
                {fxPagination.page} / {fxPagination.totalPages}
              </span>
              <button
                type="button"
                className="amf-index-page"
                disabled={!fxPagination.hasNext || fxLoading}
                onClick={() => setFxPage((p) => p + 1)}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="alg-settings-panel">
          <label className="alg-field-label">Override current season</label>
          <select
            className="alg-input"
            value={seasonYear}
            onChange={(e) => setSeasonYear(e.target.value)}
          >
            <option value="">— keep current —</option>
            {(league.seasons || []).map((s) => (
              <option key={s._id} value={s.year}>
                {s.year}
                {s.isCurrent ? " (API current)" : ""}
              </option>
            ))}
          </select>

          <label className="alg-field-label">Notes</label>
          <textarea
            className="alg-input alg-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes…"
          />

          {(league.seasons || []).length > 0 ? (
            <>
              <p className="alg-field-label">All seasons</p>
              <div className="alg-season-list">
                {(league.seasons || []).map((s) => (
                  <div key={s._id} className={`alg-season-row${s.isCurrent ? " is-current" : ""}`}>
                    <span>
                      <strong>{s.year}</strong>
                      {s.isCurrent ? " · current" : ""}
                    </span>
                    <span>
                      {formatDate(s.startDate)} → {formatDate(s.endDate)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          <div className="alg-settings-actions">
            <button type="button" className="alg-btn" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>
      )}
      </div>
    </section>
    <DisableLeagueModal
      open={disableOpen}
      leagueName={league.name}
      activity={linkedActivity}
      busy={busy}
      onCancel={() => setDisableOpen(false)}
      onForceDisable={handleForceDisable}
    />
    </>
  );
}
